import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { saveStats } from '../lib/userStats';
import { medicalSchools, matchSchools, US_STATES } from '../data/schools';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const C = {
  bg:      '#f8fafc',
  surface: '#ffffff',
  border:  '#e5e7eb',
  blue:    '#3b82f6',
  blueDk:  '#2563eb',
  indigo:  '#6366f1',
  purple:  '#8b5cf6',
  amber:   '#f59e0b',
  red:     '#ef4444',
  green:   '#10b981',
  text:    '#0f172a',
  textSec: '#64748b',
  textMut: '#94a3b8',
};

const CAT_COLOR = { safety: '#2563eb', match: '#6366f1', reach: '#f59e0b', longshot: '#94a3b8' };

const SPRINTS = [
  { id: 'sprint1', label: 'Foundation', period: 'Sep–Nov', items: [
    { id: 's1_aamc',      text: 'Create AAMC account' },
    { id: 's1_fap',       text: 'Review FAP eligibility' },
    { id: 's1_schools',   text: 'Build initial school list' },
    { id: 's1_mcat_plan', text: 'Map out MCAT study plan' },
    { id: 's1_letters',   text: 'Identify 5-6 letter writers' },
    { id: 's1_hours',     text: 'Start logging clinical hours' },
    { id: 's1_committee', text: 'Check for committee letter' },
  ]},
  { id: 'sprint2', label: 'Deep Study', period: 'Dec–Feb', items: [
    { id: 's2_fap',       text: 'Submit FAP by February 15' },
    { id: 's2_mcat_study',text: 'MCAT content review and study' },
    { id: 's2_practice',  text: 'Full-length practice exams monthly' },
    { id: 's2_letters',   text: 'Send letter of rec requests' },
    { id: 's2_committee', text: 'Apply to committee letter process' },
    { id: 's2_ps',        text: 'Begin drafting personal statement' },
  ]},
  { id: 'sprint3', label: 'Submit', period: 'Mar–May', items: [
    { id: 's3_mcat',           text: 'Take the MCAT (aim for April)' },
    { id: 's3_transcripts',    text: 'Request official transcripts' },
    { id: 's3_amcas',          text: 'Begin AMCAS application (opens May 5)' },
    { id: 's3_ps_final',       text: 'Finalize personal statement' },
    { id: 's3_activities',     text: 'Complete Work and Activities section' },
    { id: 's3_letters_confirm',text: 'Confirm all letters submitted' },
    { id: 's3_submit',         text: 'Submit AMCAS (opens May 28)' },
  ]},
  { id: 'sprint4', label: 'Secondaries', period: 'Jun–Aug', items: [
    { id: 's4_prewrite',    text: 'Pre-write secondary essays' },
    { id: 's4_casper',      text: 'Take CASPer or AAMC PREview' },
    { id: 's4_secondaries', text: 'Complete each secondary within 2 weeks' },
    { id: 's4_invites',     text: 'Watch for interview invitations' },
  ]},
  { id: 'sprint5', label: 'Interviews', period: 'Sep–Dec', items: [
    { id: 's5_schedule', text: 'Schedule interviews promptly' },
    { id: 's5_format',   text: 'Research each school interview format' },
    { id: 's5_prep',     text: 'Complete mock interviews' },
    { id: 's5_attend',   text: 'Attend interviews' },
    { id: 's5_thankyou', text: 'Send thank-you emails within 24hrs' },
  ]},
  { id: 'sprint6', label: 'Commit', period: 'Jan–May', items: [
    { id: 's6_decisions', text: 'Track all decisions in PreVisr' },
    { id: 's6_loi',       text: 'Send letter of intent if waitlisted' },
    { id: 's6_fafsa',     text: 'File FAFSA for financial aid' },
    { id: 's6_narrow',    text: 'Narrow to one acceptance by April 30' },
    { id: 's6_withdraw',  text: 'Withdraw from all other schools' },
  ]},
];

// Phase-band timeline, generated relative to the user's selected application cycle
// (e.g. "2026-2027" -> May 2026 – Mar 2027). Falls back to 2026-2027 if no cycle is set yet.
function buildTimeline(cycleYear) {
  let startYear = 2026, endYear = 2027;
  if (cycleYear && /^\d{4}-\d{4}$/.test(cycleYear)) {
    const parts = cycleYear.split('-');
    startYear = +parts[0];
    endYear = +parts[1];
  }
  const start = new Date(startYear, 4, 1);
  const end   = new Date(endYear, 2, 31);
  const phases = [
    { label: 'Applications open', start: new Date(startYear, 4,  1), end: new Date(startYear, 5, 30), color: '#3b82f6', row: 0 },
    { label: 'Secondary window',  start: new Date(startYear, 6,  1), end: new Date(startYear, 9, 31), color: '#6366f1', row: 1 },
    { label: 'Interview window',  start: new Date(startYear, 8,  1), end: new Date(endYear,   1, 28), color: '#8b5cf6', row: 2 },
    { label: 'Decisions',         start: new Date(startYear, 9,  1), end: new Date(endYear,   2, 31), color: '#2563eb', row: 3 },
  ];
  const months = [
    { label: 'May', date: new Date(startYear,  4, 1) },
    { label: 'Jun', date: new Date(startYear,  5, 1) },
    { label: 'Jul', date: new Date(startYear,  6, 1) },
    { label: 'Aug', date: new Date(startYear,  7, 1) },
    { label: 'Sep', date: new Date(startYear,  8, 1) },
    { label: 'Oct', date: new Date(startYear,  9, 1) },
    { label: 'Nov', date: new Date(startYear, 10, 1) },
    { label: 'Dec', date: new Date(startYear, 11, 1) },
    { label: 'Jan', date: new Date(endYear,    0, 1) },
    { label: 'Feb', date: new Date(endYear,    1, 1) },
    { label: 'Mar', date: new Date(endYear,    2, 1) },
  ];
  return { start, end, phases, months, startYear, endYear };
}

const TOUR_STEPS = [
  { target: 'stats',     title: 'Your stats',           desc: 'Enter your MCAT, GPA, and state of residence. PreVisr saves them automatically and uses them to match schools to your profile.' },
  { target: 'banner',   title: 'Your milestones',       desc: 'This banner tracks where you are in the application cycle and flags the most time-sensitive action right now.' },
  { target: 'metrics',  title: 'Track your progress',   desc: 'Five live metrics show how many schools you have saved, tasks completed, primaries and secondaries pending, and any missed deadlines.' },
  { target: 'timeline', title: 'Your cycle timeline',   desc: 'The timeline shows every major application phase from May through March. The red line marks where you are today.' },
  { target: 'sprints',  title: 'Stay on track',         desc: 'Six application sprints break the year into focused phases. Click any task to mark it in progress or done — your progress saves instantly.' },
  { target: 'deadlines',title: 'Never miss a deadline', desc: 'Upcoming deadlines are sorted by urgency. Secondaries you have requested but not yet submitted are flagged at the top.' },
];

function parseDeadline(str) {
  if (!str || str === 'N/A' || str === 'Rolling') return null;
  const p = str.split('/');
  if (p.length !== 3) return null;
  const d = new Date(+p[2], +p[0] - 1, +p[1]);
  return isNaN(d.getTime()) ? null : d;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function fmt(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Ring({ value, max, color, size }) {
  const s = size || 52;
  const stroke = 5;
  const r = (s - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const pct  = max > 0 ? Math.min(value / max, 1) : 0;
  return (
    <svg width={s} height={s} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={s/2} cy={s/2} r={r} fill="none" stroke={C.border} strokeWidth={stroke} />
      <circle cx={s/2} cy={s/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${circ} ${circ}`} strokeDashoffset={circ * (1 - pct)}
        strokeLinecap="round" />
    </svg>
  );
}

export default function Dashboard({ session }) {
  const isMobile = useIsMobile();
  const [loading, setLoading]                       = useState(true);
  const [sprintTasks, setSprintTasks]               = useState({});
  const [savedSchools, setSavedSchools]             = useState([]);
  const [schoolChecklists, setSchoolChecklists]     = useState({});
  const [cycleYear, setCycleYear]                   = useState(null);
  const [stats, setStats]                           = useState({ mcat: '', gpa: '', sciGpa: '', state: '' });
  const [tourStep, setTourStep]                     = useState(null);
  const [subscriptionActive, setSubscriptionActive] = useState(null);
  const [showSuccessMsg, setShowSuccessMsg]         = useState(false);
  const [upgradeLoading, setUpgradeLoading]         = useState(false);
  const [upgradeError, setUpgradeError]             = useState('');
  const saveDebounceRef                             = useRef(null);

  // Capture payment=success param on mount before session loads, store in sessionStorage
  useEffect(function() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      window.history.replaceState({}, '', '/dashboard');
      try { sessionStorage.setItem('previsr_payment_success', '1'); } catch (_) {}
    }
  }, []);

  useEffect(function() {
    if (!session) { setLoading(false); return; }
    const uid = session.user.id;
    try { if (!localStorage.getItem('previsr_tour_complete_' + uid)) setTourStep(0); } catch (_) {}
    Promise.all([
      supabase.from('pre_app_checklist').select('item_id, status').eq('user_id', uid),
      supabase.from('saved_schools').select('school_id, school_name, category').eq('user_id', uid),
      supabase.from('checklists').select('id, school_id, step, done').eq('user_id', uid),
      supabase.from('profiles').select('application_cycle, mcat, gpa, sci_gpa, state, subscription_active').eq('id', uid).single(),
    ]).then(function(results) {
      const [sprintRes, savedRes, checklistRes, profileRes] = results;

      if (!sprintRes.error && sprintRes.data) {
        const map = {};
        sprintRes.data.forEach(function(r) { map[r.item_id] = r.status || 'todo'; });
        setSprintTasks(map);
      }
      if (!savedRes.error && savedRes.data) setSavedSchools(savedRes.data);
      if (!checklistRes.error && checklistRes.data) {
        const map = {};
        checklistRes.data.forEach(function(r) {
          if (!map[r.school_id]) map[r.school_id] = {};
          map[r.school_id][r.step] = { done: r.done, rowId: r.id };
        });
        setSchoolChecklists(map);
      }
      if (!profileRes.error && profileRes.data) {
        setCycleYear(profileRes.data.application_cycle);
        setSubscriptionActive(profileRes.data.subscription_active === true);
        const s = {
          mcat:   profileRes.data.mcat   != null ? String(profileRes.data.mcat)   : '',
          gpa:    profileRes.data.gpa    != null ? String(profileRes.data.gpa)    : '',
          sciGpa: profileRes.data.sci_gpa!= null ? String(profileRes.data.sci_gpa): '',
          state:  profileRes.data.state  || '',
        };
        setStats(s);
        try { localStorage.setItem('previsr_stats_' + uid, JSON.stringify(s)); } catch (_) {}
      } else {
        setSubscriptionActive(false);
        try {
          const cached = JSON.parse(localStorage.getItem('previsr_stats_' + uid) || '{}');
          if (cached && (cached.mcat || cached.gpa)) setStats(cached);
        } catch (_) {}
      }
      setLoading(false);
    }).catch(function() { setSubscriptionActive(false); setLoading(false); });
    return function() { if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current); };
  }, [session]);

  // After session is available, check for stored payment success flag and re-fetch subscription
  useEffect(function() {
    if (!session) return;
    let paymentSuccess = false;
    try { paymentSuccess = sessionStorage.getItem('previsr_payment_success') === '1'; } catch (_) {}
    if (!paymentSuccess) return;
    try { sessionStorage.removeItem('previsr_payment_success'); } catch (_) {}
    setShowSuccessMsg(true);
    const timer = setTimeout(async function() {
      const { data } = await supabase
        .from('profiles')
        .select('subscription_active')
        .eq('id', session.user.id)
        .single();
      if (data) setSubscriptionActive(data.subscription_active === true);
      setShowSuccessMsg(false);
    }, 4000);
    return function() { clearTimeout(timer); };
  }, [session]);

  function handleStatChange(field, value) {
    const next = Object.assign({}, stats, { [field]: value });
    setStats(next);
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(function() {
      if (session) saveStats(session.user.id, next);
    }, 500);
  }

  function dismissTour() {
    setTourStep(null);
    try { if (session) localStorage.setItem('previsr_tour_complete_' + session.user.id, '1'); } catch (_) {}
  }
  function advanceTour() {
    if (tourStep >= TOUR_STEPS.length - 1) dismissTour();
    else setTourStep(function(s) { return s + 1; });
  }
  function tourTarget(tgt) {
    if (tourStep === null || TOUR_STEPS[tourStep]?.target !== tgt) return {};
    return { position: 'relative', zIndex: 201, boxShadow: '0 0 0 3px #3b82f6, 0 0 20px rgba(59,130,246,0.25)', borderRadius: 10 };
  }
  function tourTargetFull(tgt) {
    if (tourStep === null || TOUR_STEPS[tourStep]?.target !== tgt) return {};
    return { position: 'relative', zIndex: 201, boxShadow: '0 0 0 3px #3b82f6, 0 0 20px rgba(59,130,246,0.25)' };
  }

  async function handleUpgrade() {
    if (!session) return;
    setUpgradeLoading(true);
    setUpgradeError('');
    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: session.user.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
      window.location.href = data.url;
    } catch (err) {
      setUpgradeError(err.message || 'Something went wrong. Please try again.');
      setUpgradeLoading(false);
    }
  }

  // ── Derived ──────────────────────────────────────────────────────────────────

  const today = useMemo(function() {
    const d = new Date(); d.setHours(0,0,0,0); return d;
  }, []);

  const timeline = useMemo(function() { return buildTimeline(cycleYear); }, [cycleYear]);
  function tl2Pct(date) {
    return Math.max(0, Math.min(100, ((date - timeline.start) / (timeline.end - timeline.start)) * 100));
  }

  const hasStats = !!(stats.mcat && stats.gpa && stats.state);

  const firstName = session?.user?.user_metadata?.full_name
    ? session.user.user_metadata.full_name.split(' ')[0]
    : 'there';

  function sprintDone(sprint)  { return sprint.items.every(function(i) { return sprintTasks[i.id] === 'done'; }); }
  function sprintProg(sprint)  {
    const done = sprint.items.filter(function(i) { return sprintTasks[i.id] === 'done'; }).length;
    return { done, total: sprint.items.length, pct: sprint.items.length ? Math.round(done / sprint.items.length * 100) : 0 };
  }
  function isSprintBehind(idx) {
    if (sprintDone(SPRINTS[idx])) return false;
    const amcas = new Date(timeline.startYear, 4, 28);
    const jun11 = new Date(timeline.startYear, 5, 11);
    const aug1  = new Date(timeline.startYear, 7, 1);
    if (idx === 0 || idx === 1) return today >= amcas;
    if (idx === 2) return today >= jun11;
    if (idx === 3) return today >= aug1;
    return false;
  }

  const allSprintItems = SPRINTS.reduce(function(acc, s) { return acc + s.items.length; }, 0);
  const allSprintDone  = SPRINTS.reduce(function(acc, s) {
    return acc + s.items.filter(function(i) { return sprintTasks[i.id] === 'done'; }).length;
  }, 0);

  // Single matchSchools call — derive both matchById and topMatchedSchools from the same results array
  const matchResults = useMemo(function() {
    if (!hasStats) return [];
    return matchSchools({
      mcat: +stats.mcat, gpa: +stats.gpa,
      sciGpa: stats.sciGpa ? +stats.sciGpa : null,
      state: stats.state,
      degreeTypes: ['MD','DO'], regions: ['US','Canada','Caribbean','Puerto Rico'],
    });
  }, [hasStats, stats]);

  const matchById = useMemo(function() {
    const m = new Map();
    matchResults.forEach(function(r) { m.set(r.id, r); });
    return m;
  }, [matchResults]);

  const topMatchedSchools = useMemo(function() {
    return matchResults.slice(0, 5);
  }, [matchResults]);

  const listCounts = useMemo(function() {
    const c = { safety: 0, match: 0, reach: 0, longshot: 0 };
    savedSchools.forEach(function(s) {
      const m = matchById.get(s.school_id);
      if (m && c[m.category] !== undefined) c[m.category]++;
    });
    return c;
  }, [savedSchools, matchById]);

  const primariesSent = useMemo(function() {
    return savedSchools.filter(function(s) {
      return !!((schoolChecklists[s.school_id] || {})['Primary submitted']?.done);
    }).length;
  }, [savedSchools, schoolChecklists]);

  const secondariesReceived = useMemo(function() {
    return savedSchools.filter(function(s) {
      return !!((schoolChecklists[s.school_id] || {})['Secondary requested']?.done);
    }).length;
  }, [savedSchools, schoolChecklists]);

  const interviewsReceived = useMemo(function() {
    return savedSchools.filter(function(s) {
      return !!((schoolChecklists[s.school_id] || {})['Interview invite']?.done);
    }).length;
  }, [savedSchools, schoolChecklists]);

  const upcomingPrimariesList = useMemo(function() {
    return savedSchools.map(function(s) {
      const school = medicalSchools.find(function(m) { return m.id === s.school_id; });
      if (!school) return null;
      const d = parseDeadline(school.primaryDeadline);
      if (!d || d <= today) return null;
      if ((schoolChecklists[s.school_id] || {})['Primary submitted']?.done) return null;
      return { school, d, days: Math.ceil((d - today) / 86400000), type: 'primary' };
    }).filter(Boolean).sort(function(a,b) { return a.d - b.d; });
  }, [savedSchools, schoolChecklists, today]);

  const urgentSecondaries = useMemo(function() {
    return savedSchools.map(function(s) {
      const school = medicalSchools.find(function(m) { return m.id === s.school_id; });
      if (!school) return null;
      const c = schoolChecklists[s.school_id] || {};
      if (!c['Secondary requested']?.done) return null;
      if (c['Secondary submitted']?.done) return null;
      const d = parseDeadline(school.secondaryDeadline);
      const days = d ? Math.ceil((d - today) / 86400000) : null;
      return { school, d, days, type: 'secondary-urgent' };
    }).filter(Boolean).sort(function(a,b) { return (a.days??999) - (b.days??999); });
  }, [savedSchools, schoolChecklists, today]);

  const upcomingSecondariesList = useMemo(function() {
    const urgentIds = new Set(urgentSecondaries.map(function(u) { return u.school.id; }));
    return savedSchools.map(function(s) {
      const school = medicalSchools.find(function(m) { return m.id === s.school_id; });
      if (!school || urgentIds.has(school.id)) return null;
      const d = parseDeadline(school.secondaryDeadline);
      if (!d || d <= today) return null;
      if ((schoolChecklists[s.school_id] || {})['Secondary submitted']?.done) return null;
      return { school, d, days: Math.ceil((d - today) / 86400000), type: 'secondary' };
    }).filter(Boolean).sort(function(a,b) { return a.d - b.d; });
  }, [savedSchools, schoolChecklists, today, urgentSecondaries]);

  const deadlineList = useMemo(function() {
    return urgentSecondaries.concat(upcomingPrimariesList).concat(upcomingSecondariesList).slice(0, 20);
  }, [urgentSecondaries, upcomingPrimariesList, upcomingSecondariesList]);

  const amcasOpen = new Date(timeline.startYear, 4, 28);
  const amcasEnd  = new Date(timeline.startYear, 6, 31);
  let bannerText, bannerSub;
  if (today < amcasOpen) {
    const days = Math.ceil((amcasOpen - today) / 86400000);
    bannerText = `AMCAS opens for submission in ${days} day${days !== 1 ? 's' : ''}`;
    bannerSub  = 'Use this time to finalize your personal statement and confirm all letter writers are on track.';
  } else if (today <= amcasEnd) {
    const days = Math.ceil((today - amcasOpen) / 86400000);
    bannerText = `AMCAS has been open for ${days} day${days !== 1 ? 's' : ''} — earlier submissions have a meaningful advantage`;
    bannerSub  = 'Submit as soon as possible. Schools review on a rolling basis starting June 26.';
  } else {
    bannerText = 'Primary season is closing — submit remaining applications immediately';
    bannerSub  = 'Late submissions face steep rolling admissions disadvantages. Do not wait.';
  }

  async function cycleSprintTask(itemId) {
    const current = sprintTasks[itemId] || 'todo';
    const next    = current === 'todo' ? 'inprogress' : current === 'inprogress' ? 'done' : 'todo';
    setSprintTasks(function(prev) { return Object.assign({}, prev, { [itemId]: next }); });
    const { error } = await supabase.from('pre_app_checklist').upsert(
      { user_id: session.user.id, item_id: itemId, status: next, done: next === 'done' },
      { onConflict: 'user_id,item_id' }
    );
    if (error) setSprintTasks(function(prev) { return Object.assign({}, prev, { [itemId]: current }); });
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
        <div style={{ width: 26, height: 26, border: `3px solid ${C.border}`, borderTopColor: C.blue, borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
      </div>
    );
  }

  const dateStr  = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const listTotal = listCounts.safety + listCounts.match + listCounts.reach + listCounts.longshot;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg, minHeight: '100vh' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Tour overlay (premium only) ──────────────────────────────────────── */}
      {tourStep !== null && subscriptionActive && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.52)', zIndex: 200, pointerEvents: 'none' }} />
          <div style={{ position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 202, background: C.surface, borderRadius: 14, padding: '22px 26px', width: isMobile ? 'calc(100vw - 32px)' : 380, boxShadow: '0 8px 32px rgba(0,0,0,0.22)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 8 }}>
              Step {tourStep + 1} of {TOUR_STEPS.length}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8 }}>
              {TOUR_STEPS[tourStep].title}
            </div>
            <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.65, marginBottom: 20 }}>
              {TOUR_STEPS[tourStep].desc}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <button onClick={dismissTour} style={{ background: 'none', border: 'none', fontSize: 12, color: C.textMut, cursor: 'pointer', padding: 0 }}>
                Skip tour
              </button>
              <button onClick={advanceTour} style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 22px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {tourStep === TOUR_STEPS.length - 1 ? 'Done' : 'Next'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 16 }}>
              {TOUR_STEPS.map(function(_, i) {
                return <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: i === tourStep ? C.blue : C.border }} />;
              })}
            </div>
          </div>
        </>
      )}

      {/* ── Toast messages ───────────────────────────────────────────────────── */}
      {(showSuccessMsg || upgradeError) && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 300, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 340 }}>
          {showSuccessMsg && (
            <div style={{ background: C.green, color: '#fff', borderRadius: 10, padding: '14px 18px', fontSize: 13, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', lineHeight: 1.4 }}>
              Payment successful — activating your subscription...
            </div>
          )}
          {upgradeError && (
            <div style={{ background: C.red, color: '#fff', borderRadius: 10, padding: '14px 18px', fontSize: 13, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, lineHeight: 1.4 }}>
              <span>{upgradeError}</span>
              <button onClick={function() { setUpgradeError(''); }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 0, opacity: 0.8, flexShrink: 0 }}>×</button>
            </div>
          )}
        </div>
      )}

      {/* ── Topbar ─────────────────────────────────────────────────────────── */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: isMobile ? '14px 16px' : '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div>
          <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: C.text, letterSpacing: '-0.3px' }}>
            {getGreeting()}, {firstName}
          </div>
          <div style={{ fontSize: 11, color: C.textMut, marginTop: 2 }}>
            {isMobile ? '' : dateStr}{cycleYear && <span style={{ color: C.textSec }}>{isMobile ? cycleYear + ' cycle' : ' · ' + cycleYear + ' cycle'}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!subscriptionActive && (
            <button
              onClick={handleUpgrade}
              disabled={upgradeLoading}
              style={{ padding: '8px 16px', background: C.amber, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: upgradeLoading ? 'not-allowed' : 'pointer', opacity: upgradeLoading ? 0.7 : 1 }}
            >
              {upgradeLoading ? 'Loading...' : isMobile ? 'Upgrade' : 'Upgrade to Premium'}
            </button>
          )}
          <Link to="/matcher" style={{ padding: '8px 16px', background: C.blue, color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
            {isMobile ? 'Matcher' : 'School Matcher'}
          </Link>
        </div>
      </div>

      {/* ── Stats input bar (always visible) ────────────────────────────────── */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, borderLeft: `3px solid ${C.blue}`, padding: isMobile ? '12px 16px' : '12px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', ...tourTargetFull('stats') }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {[
            { label: 'MCAT Score',     field: 'mcat',   type: 'number', min: 472, max: 528, step: 1,    placeholder: '518' },
            { label: 'Cumulative GPA', field: 'gpa',    type: 'number', min: 0,   max: 4,   step: 0.01, placeholder: '3.80' },
            { label: 'Science GPA',    field: 'sciGpa', type: 'number', min: 0,   max: 4,   step: 0.01, placeholder: '3.75' },
          ].map(function(inp) {
            return (
              <div key={inp.field} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', color: C.text }}>{inp.label}</div>
                <input
                  type={inp.type} min={inp.min} max={inp.max} step={inp.step} placeholder={inp.placeholder}
                  value={stats[inp.field]}
                  onChange={function(e) { handleStatChange(inp.field, e.target.value); }}
                  style={{ width: 90, padding: '6px 10px', fontSize: 15, fontWeight: 600, color: C.text, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, outline: 'none' }}
                />
              </div>
            );
          })}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', color: C.text }}>State of Residence</div>
            <select value={stats.state} onChange={function(e) { handleStatChange('state', e.target.value); }}
              style={{ padding: '6px 10px', fontSize: 15, fontWeight: 600, color: stats.state ? C.text : C.textMut, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, outline: 'none', cursor: 'pointer', minWidth: 90 }}>
              <option value="">State</option>
              {US_STATES.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
            </select>
          </div>
          <div style={{ marginLeft: 'auto', alignSelf: 'flex-end', paddingBottom: 4, fontSize: 10, color: C.textMut }}>Saved automatically</div>
        </div>
      </div>

      {/* ── Conditional content ──────────────────────────────────────────────── */}
      {subscriptionActive ? (

        // ── Full premium dashboard ──────────────────────────────────────────
        <>
          {/* Urgency banner */}
          <div style={{ background: '#1e40af', padding: isMobile ? '11px 16px' : '11px 24px', display: 'flex', alignItems: 'center', gap: 14, ...tourTargetFull('banner') }}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#93c5fd" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r=".5" fill="#93c5fd"/></svg>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.4 }}>{bannerText}</div>
              <div style={{ fontSize: 11, color: '#93c5fd', marginTop: 2 }}>{bannerSub}</div>
            </div>
          </div>

          <div style={{ padding: isMobile ? '16px' : '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Metrics row */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: 12, ...tourTarget('metrics') }}>

              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
                  <Ring value={savedSchools.length} max={25} color={C.blue} size={52} />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: C.text }}>{savedSchools.length}</div>
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{savedSchools.length}</div>
                  <div style={{ fontSize: 11, color: C.textSec }}>Schools saved</div>
                  <div style={{ fontSize: 10, color: C.textMut }}>of 25 target</div>
                </div>
              </div>

              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
                  <Ring value={allSprintDone} max={allSprintItems} color={C.indigo} size={52} />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: C.text }}>
                    {allSprintItems > 0 ? Math.round(allSprintDone/allSprintItems*100) : 0}%
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{allSprintDone}<span style={{ fontSize: 12, fontWeight: 500, color: C.textMut }}>/{allSprintItems}</span></div>
                  <div style={{ fontSize: 11, color: C.textSec }}>Tasks done</div>
                  <div style={{ fontSize: 10, color: C.textMut }}>across all sprints</div>
                </div>
              </div>

              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.blue}`, borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: C.text, lineHeight: 1 }}>{upcomingPrimariesList.length}</div>
                <div style={{ fontSize: 11, color: C.textSec, marginTop: 4 }}>Upcoming primaries</div>
                <div style={{ fontSize: 10, color: C.textMut, marginTop: 2 }}>not yet submitted</div>
              </div>

              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.indigo}`, borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: C.text, lineHeight: 1 }}>{urgentSecondaries.length + upcomingSecondariesList.length}</div>
                <div style={{ fontSize: 11, color: C.textSec, marginTop: 4 }}>Secondaries</div>
                <div style={{ fontSize: 10, color: C.textMut, marginTop: 2 }}>
                  {urgentSecondaries.length > 0 ? `${urgentSecondaries.length} urgent` : 'pending'}
                </div>
              </div>

              {(function() {
                const missed = savedSchools.filter(function(s) {
                  const school = medicalSchools.find(function(m) { return m.id === s.school_id; });
                  if (!school) return false;
                  const d = parseDeadline(school.primaryDeadline);
                  if (!d || d > today) return false;
                  return !((schoolChecklists[s.school_id] || {})['Primary submitted']?.done);
                }).length;
                return (
                  <div style={{ background: missed > 0 ? '#fef2f2' : C.surface, border: `1px solid ${missed > 0 ? '#fecaca' : C.border}`, borderLeft: `3px solid ${missed > 0 ? C.red : C.border}`, borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ fontSize: 26, fontWeight: 700, color: missed > 0 ? C.red : C.text, lineHeight: 1 }}>{missed}</div>
                    <div style={{ fontSize: 11, color: C.textSec, marginTop: 4 }}>Missed deadlines</div>
                    <div style={{ fontSize: 10, color: C.textMut, marginTop: 2 }}>primary not submitted</div>
                  </div>
                );
              })()}
            </div>

            {/* Charts row */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>

              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>List Balance</div>
                <div style={{ fontSize: 11, color: C.textSec, marginBottom: 16 }}>Target: 5-7 schools each in Target, Reach; 8-10 in Match</div>
                {!hasStats ? (
                  <div style={{ fontSize: 12, color: C.textMut, textAlign: 'center', padding: '24px 0' }}>Enter your stats above to see list balance</div>
                ) : (
                  <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                    <div style={{ position: 'relative', width: 88, height: 88, flexShrink: 0 }}>
                      <div style={{ width: 88, height: 88, borderRadius: '50%', background: listTotal === 0 ? C.border : `conic-gradient(${CAT_COLOR.safety} 0deg ${listTotal>0?(listCounts.safety/listTotal*360):0}deg, ${CAT_COLOR.match} ${listTotal>0?(listCounts.safety/listTotal*360):0}deg ${listTotal>0?((listCounts.safety+listCounts.match)/listTotal*360):0}deg, ${CAT_COLOR.reach} ${listTotal>0?((listCounts.safety+listCounts.match)/listTotal*360):0}deg ${listTotal>0?((listCounts.safety+listCounts.match+listCounts.reach)/listTotal*360):0}deg, ${CAT_COLOR.longshot} ${listTotal>0?((listCounts.safety+listCounts.match+listCounts.reach)/listTotal*360):0}deg 360deg)` }} />
                      <div style={{ position: 'absolute', inset: 20, borderRadius: '50%', background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{listTotal}</div>
                      </div>
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { key: 'safety', label: 'Target', need: '5-7'  },
                        { key: 'match',  label: 'Match',  need: '8-10' },
                        { key: 'reach',  label: 'Reach',  need: '5-7'  },
                      ].map(function(row) {
                        const count = listCounts[row.key] || 0;
                        const max   = row.key === 'match' ? 10 : 7;
                        return (
                          <div key={row.key}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <div style={{ width: 8, height: 8, borderRadius: 2, background: CAT_COLOR[row.key] }} />
                                <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{row.label}</span>
                              </div>
                              <span style={{ fontSize: 10, color: C.textSec }}>{count} <span style={{ color: C.textMut }}>/ {row.need}</span></span>
                            </div>
                            <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${Math.min(count/max*100,100)}%`, height: '100%', background: CAT_COLOR[row.key], borderRadius: 3 }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>Application Pipeline</div>
                <div style={{ fontSize: 11, color: C.textSec, marginBottom: 16 }}>Checklist progress across saved schools</div>
                {savedSchools.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.textMut, textAlign: 'center', padding: '24px 0' }}>Save schools to track your pipeline</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 10 }}>
                    {[
                      { label: 'Primaries sent',        count: primariesSent,       color: C.blue   },
                      { label: 'Secondaries requested', count: secondariesReceived,  color: C.indigo },
                      { label: 'Interviews received',   count: interviewsReceived,   color: C.purple },
                    ].map(function(box) {
                      return (
                        <div key={box.label} style={{ background: C.bg, border: `1px solid ${C.border}`, borderLeft: `3px solid ${box.color}`, borderRadius: 8, padding: '12px 14px' }}>
                          <div style={{ fontSize: 28, fontWeight: 700, color: C.text, lineHeight: 1 }}>{box.count}</div>
                          <div style={{ fontSize: 10, color: C.textSec, marginTop: 6, lineHeight: 1.4 }}>{box.label}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Cycle timeline */}
            {!isMobile && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px', ...tourTarget('timeline') }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 16 }}>Application Cycle Timeline · {timeline.startYear}-{timeline.endYear}</div>
              <div style={{ position: 'relative', height: 128 }}>
                {timeline.phases.map(function(phase) {
                  const left  = tl2Pct(phase.start);
                  const width = tl2Pct(phase.end) - left;
                  return (
                    <div key={phase.label} style={{
                      position: 'absolute', top: phase.row * 24, left: left + '%', width: width + '%',
                      height: 20, background: phase.color + '22', border: `1px solid ${phase.color}55`,
                      borderRadius: 4, display: 'flex', alignItems: 'center', overflow: 'hidden', boxSizing: 'border-box',
                    }}>
                      <span style={{ fontSize: 9, fontWeight: 700, color: phase.color, paddingLeft: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {phase.label}
                      </span>
                    </div>
                  );
                })}
                {(function() {
                  const pct = tl2Pct(today);
                  if (pct < 0 || pct > 100) return null;
                  return (
                    <div style={{ position: 'absolute', top: 0, bottom: 28, left: pct + '%', width: 2, background: C.red, transform: 'translateX(-50%)', zIndex: 2 }}>
                      <div style={{ position: 'absolute', top: -16, left: '50%', transform: 'translateX(-50%)', fontSize: 9, fontWeight: 700, color: C.red, whiteSpace: 'nowrap', background: C.surface, padding: '1px 4px', borderRadius: 3, border: `1px solid #fecaca` }}>
                        Today
                      </div>
                    </div>
                  );
                })()}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 24 }}>
                  {timeline.months.map(function(m) {
                    const pct = tl2Pct(m.date);
                    return (
                      <div key={m.label} style={{ position: 'absolute', left: pct + '%', transform: 'translateX(-50%)', bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 1, height: 5, background: C.border, marginBottom: 2 }} />
                        <div style={{ fontSize: 9, color: C.textMut, whiteSpace: 'nowrap' }}>{m.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            )}

            {/* Sprint cards */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', ...tourTarget('sprints') }}>
              <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Application Sprints</div>
                <div style={{ fontSize: 11, color: C.textSec, marginTop: 2 }}>{allSprintDone}/{allSprintItems} tasks complete</div>
              </div>
              <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12 }}>
                {SPRINTS.map(function(sprint, idx) {
                  const prog   = sprintProg(sprint);
                  const behind = isSprintBehind(idx);
                  const done   = sprintDone(sprint);
                  const barColor = behind && !done ? C.red : done ? '#22c55e' : C.blue;
                  return (
                    <div key={sprint.id} style={{
                      border: `1px solid ${behind && !done ? '#fecaca' : done ? '#bbf7d0' : C.border}`,
                      background: behind && !done ? '#fef9f9' : C.bg,
                      borderRadius: 8, padding: '12px 13px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        {behind && !done && (
                          <span style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', padding: '1px 5px', borderRadius: 3, background: '#fef2f2', color: C.red, border: '0.5px solid #fecaca', flexShrink: 0 }}>Behind</span>
                        )}
                        {done && (
                          <span style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', padding: '1px 5px', borderRadius: 3, background: '#f0fdf4', color: '#16a34a', border: '0.5px solid #bbf7d0', flexShrink: 0 }}>Done</span>
                        )}
                        <span style={{ fontSize: 11, fontWeight: 700, color: C.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {idx + 1}. {sprint.label}
                        </span>
                        <span style={{ fontSize: 10, color: C.textMut, flexShrink: 0 }}>{prog.pct}%</span>
                      </div>
                      <div style={{ fontSize: 9, color: C.textMut, marginBottom: 8 }}>{sprint.period}</div>
                      <div style={{ height: 3, background: C.border, borderRadius: 2, marginBottom: 10, overflow: 'hidden' }}>
                        <div style={{ width: `${prog.pct}%`, height: '100%', background: barColor, borderRadius: 2 }} />
                      </div>
                      <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {sprint.items.map(function(item) {
                          return <TaskRow key={item.id} item={item} status={sprintTasks[item.id] || 'todo'} onCycle={cycleSprintTask} />;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Upcoming deadlines */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', ...tourTarget('deadlines') }}>
              <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Upcoming Deadlines</div>
                <div style={{ fontSize: 11, color: C.textSec, marginTop: 2 }}>Sorted by urgency</div>
              </div>
              <div style={{ maxHeight: 360, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {deadlineList.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.textMut, textAlign: 'center', padding: '32px 0' }}>
                    {savedSchools.length === 0 ? 'Save schools to track deadlines' : 'No upcoming deadlines'}
                  </div>
                ) : deadlineList.map(function(item) {
                  const isUrgent  = item.type === 'secondary-urgent';
                  const isPrimary = item.type === 'primary';
                  const accentColor = isUrgent ? C.red : isPrimary ? C.blue : C.indigo;
                  const urgentLabel = isUrgent ? 'Requested — submit within 2 weeks' : isPrimary ? 'Primary' : 'Secondary';
                  return (
                    <Link key={item.school.id + item.type} to={`/school/${item.school.id}`}
                      style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8, background: isUrgent ? '#fef2f2' : C.bg, border: `1px solid ${isUrgent ? '#fecaca' : C.border}`, borderLeft: `3px solid ${accentColor}` }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.school.name}</div>
                        <div style={{ fontSize: 10, color: isUrgent ? C.red : C.textSec, marginTop: 2 }}>
                          {urgentLabel}{item.d && !isUrgent ? ` · ${fmt(item.d)}` : ''}
                        </div>
                      </div>
                      {item.days != null && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: item.days < 14 ? C.red : accentColor, flexShrink: 0 }}>{item.days}d</div>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>

          </div>
        </>

      ) : (

        // ── Paywall view ────────────────────────────────────────────────────
        <div style={{ padding: isMobile ? '16px' : '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Top 5 matched schools preview */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Your Top Matched Schools</div>
              <div style={{ fontSize: 11, color: C.textSec, marginTop: 2 }}>
                {hasStats ? 'Based on your stats — upgrade for full list and analysis' : 'Enter your stats above to see your matches'}
              </div>
            </div>
            {!hasStats ? (
              <div style={{ padding: '36px 20px', textAlign: 'center', fontSize: 12, color: C.textMut }}>
                Enter your MCAT, GPA, and state above to see matched schools.
              </div>
            ) : topMatchedSchools.length === 0 ? (
              <div style={{ padding: '36px 20px', textAlign: 'center', fontSize: 12, color: C.textMut }}>
                No matches found. Try adjusting your stats.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {topMatchedSchools.map(function(school, idx) {
                  const catLabel = school.category === 'safety' ? 'Target' : school.category.charAt(0).toUpperCase() + school.category.slice(1);
                  return (
                    <div key={school.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: idx < topMatchedSchools.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: C.bg, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: C.textSec, flexShrink: 0 }}>
                        {idx + 1}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{school.name}</div>
                      </div>
                      <div style={{ flexShrink: 0, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: CAT_COLOR[school.category] + '18', color: CAT_COLOR[school.category], border: `1px solid ${CAT_COLOR[school.category]}44` }}>
                        {catLabel}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Paywall card */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #4f46e5 100%)', padding: isMobile ? '24px 20px' : '28px 32px' }}>
              <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
                Unlock the full PreVisr dashboard
              </div>
              <div style={{ fontSize: 13, color: '#bfdbfe', lineHeight: 1.65, maxWidth: 500 }}>
                Everything you need to manage your medical school application from start to finish — deadlines, sprints, analytics, and more.
              </div>
            </div>
            <div style={{ padding: isMobile ? '20px' : '24px 32px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 24 }}>
                {[
                  { title: 'Application metrics',  desc: 'Live counts of primaries, secondaries, and interviews across all saved schools' },
                  { title: 'Cycle timeline',        desc: 'Visual timeline of every major application phase from May through March' },
                  { title: '6-sprint roadmap',      desc: 'Structured checklist across Foundation, Submit, Secondaries, Interviews, and more' },
                  { title: 'Deadline tracker',      desc: 'Urgency-sorted deadlines so nothing slips — urgent secondaries flagged first' },
                ].map(function(feature) {
                  return (
                    <div key={feature.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <polyline points="1,5 4,8 9,2" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{feature.title}</div>
                        <div style={{ fontSize: 11, color: C.textSec, marginTop: 2, lineHeight: 1.5 }}>{feature.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                onClick={handleUpgrade}
                disabled={upgradeLoading}
                style={{ width: '100%', padding: '13px 24px', background: upgradeLoading ? C.textMut : C.blue, color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: upgradeLoading ? 'not-allowed' : 'pointer', letterSpacing: '-0.2px' }}
              >
                {upgradeLoading ? 'Redirecting to checkout...' : 'Upgrade to Premium'}
              </button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

function TaskRow({ item, status, onCycle }) {
  const isDone = status === 'done';
  const isIP   = status === 'inprogress';
  return (
    <div onClick={function() { onCycle(item.id); }}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 7, padding: '5px 6px', borderRadius: 6, cursor: 'pointer', transition: 'background .1s', userSelect: 'none' }}>
      <div style={{ flexShrink: 0, marginTop: 1, width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${isDone ? C.blue : isIP ? '#f59e0b' : C.border}`, background: isDone ? C.blue : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isDone && <svg width="7" height="7" viewBox="0 0 10 10" fill="none"><polyline points="1,5 4,8 9,2" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/></svg>}
        {isIP  && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#f59e0b' }} />}
      </div>
      <div style={{ fontSize: 11, color: isDone ? C.textMut : C.text, lineHeight: 1.4, flex: 1 }}>{item.text}</div>
    </div>
  );
}
